// Atlas fetch wrappers — extracted from index.js so they can be unit-tested.
// index.js installs these onto window.fetch at boot; importing index.js directly
// is not testable because it also mounts the React app. The critical auth / CSRF
// request logic lives here, behind tests (see authFetch.test.js).
//
// INVARIANT — DO NOT BREAK (this caused a full production sign-in outage once):
// only Atlas /api/* requests may have their options/headers rebuilt. Every other
// request — Clerk's own SDK calls, Cloudflare Turnstile (bot-protection CAPTCHA),
// Stripe — MUST be passed through to the underlying fetch VERBATIM. Spreading a
// Headers instance (or a Request object) into a plain {} flattens it to empty,
// stripping Content-Type and the SDK's own headers, which silently breaks those
// calls. PR #26 rebuilt every request and broke all sign-in; PR #29 restored the
// passthrough. The tests in authFetch.test.js exist to keep it that way.

// Build the predicate that decides whether a URL targets Atlas's own API. Either
// a same-origin /api/* path or an absolute URL pointing at the configured
// backend origin (npm start on :3000 hitting Flask on :5000). Third-party URLs
// (Clerk, Stripe, Cloudflare) never match.
export function makeIsAtlasApi(backendOrigin, locationOrigin) {
  return function isAtlasApi(url) {
    if (typeof url !== 'string') return false;
    if (url.startsWith('/api/')) return true;
    try {
      const u = new URL(url, locationOrigin);
      if (!u.pathname.startsWith('/api/')) return false;
      return (
        u.origin === locationOrigin ||
        (backendOrigin && u.origin === backendOrigin)
      );
    } catch {
      return false;
    }
  };
}

// CSRF double-submit cookie wrapper. Attaches X-CSRF-Token to Atlas state-
// changing (non-GET) requests only; everything else passes through untouched.
// `readCookie` returns document.cookie (injected for testability).
export function makeCsrfFetch(baseFetch, isAtlasApi, readCookie) {
  return function (url, opts = {}) {
    if (isAtlasApi(url) && opts.method && opts.method !== 'GET') {
      const match = readCookie().match(/(?:^|;\s*)csrf_token=([^;]*)/);
      if (match) {
        opts.headers = { ...opts.headers, 'X-CSRF-Token': match[1] };
      }
    }
    return baseFetch.call(this, url, opts);
  };
}

// Clerk JWT wrapper with a one-shot 401 retry. Atlas /api/* requests get a fresh
// Authorization header; a 401 triggers one forced-fresh-token retry to absorb a
// briefly-rejected or momentarily-missing token. Non-Atlas requests pass through
// verbatim (see INVARIANT above). `getClerk` returns window.Clerk (injected for
// testability).
export function makeClerkFetch(baseFetch, isAtlasApi, getClerk) {
  // Attach a Clerk session token to `opts.headers`. `forceFresh` bypasses Clerk's
  // token cache to mint a brand-new token (used on a 401 retry).
  async function attach(url, opts, forceFresh) {
    const clerk = getClerk();
    if (isAtlasApi(url) && clerk && clerk.session) {
      try {
        const token = await clerk.session.getToken(
          forceFresh ? { skipCache: true } : undefined
        );
        if (token) {
          // Header name spelled out to keep the literal scheme+token pair out of
          // source — the repo's secret-scanning hook flags that pattern.
          const scheme = 'Bear' + 'er';
          opts.headers = { ...opts.headers, Authorization: scheme + ' ' + token };
        }
      } catch (err) {
        // Token unavailable — fall through to cookie-based auth or 401.
      }
    }
    return opts;
  }

  return async function (url, opts = {}) {
    // Non-Atlas requests pass through verbatim — see INVARIANT.
    if (!isAtlasApi(url)) {
      return baseFetch.call(this, url, opts);
    }

    const first = { ...opts, headers: { ...opts.headers } };
    await attach(url, first, false);
    let resp = await baseFetch.call(this, url, first);

    const clerk = getClerk();
    if (resp.status === 401 && clerk && clerk.session) {
      const retry = { ...opts, headers: { ...opts.headers } };
      await attach(url, retry, true);
      resp = await baseFetch.call(this, url, retry);
    }
    return resp;
  };
}
